/**
 * @fileoverview Curated "start from template" gallery for the document builder (Phase 2, Workstream E3).
 * @module ui-admin/components/DocumentTemplateGallery
 *
 * Responsibilities: - Present curated starter templates (grouped by category) as selectable cards,
 *                     plus a "Start blank" option, so a new document can begin from a polished base.
 * Dependencies:     - none (pure presentational; host supplies items + factories).
 * Architecture:     - Editor-agnostic, shared by tenant-web and system-web. The host maps a selected
 *                     template id to its config factory; this component owns no template data.
 * Notes:            - Curated preset definitions live in @firesquirrel/core-domain
 *                     (DOCUMENT_TEMPLATE_PRESETS); the host passes them in as `items`.
 */

import React, { useMemo } from 'react';

/**
 * @contract DocumentTemplateGalleryItem
 * Purpose: One curated starter template shown as a card.
 * Fields:  id, name, description, category.
 * Rules:   id must be unique; category groups cards.
 * Sync:    N/A (presentation input).
 */
export interface DocumentTemplateGalleryItem {
  id: string;
  name: string;
  description: string;
  category: string;
}

/**
 * @contract DocumentTemplateGalleryProps
 * Purpose: Inputs for the starter-template gallery.
 * Fields:  items (curated starters), onSelect(id), onStartBlank?, title?.
 * Rules:   onSelect fires with the chosen item id; the host applies its config factory.
 * Sync:    N/A.
 */
export interface DocumentTemplateGalleryProps {
  items: DocumentTemplateGalleryItem[];
  onSelect: (id: string) => void;
  onStartBlank?: () => void;
  title?: string;
}

/**
 * @component DocumentTemplateGallery
 * Purpose:     Let an author start a new document from a curated, attractive template.
 * Props:       items, onSelect, onStartBlank?, title?
 * Behavior:    Groups items by category; clicking a card calls onSelect(id).
 * Data Source: none (presentational; host supplies items).
 * Constraints: no API calls, no Dexie reads.
 * Performance: grouping memoized on items.
 */
export function DocumentTemplateGallery({ items, onSelect, onStartBlank, title }: DocumentTemplateGalleryProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, DocumentTemplateGalleryItem[]>();
    for (const item of items) {
      const list = map.get(item.category) ?? [];
      list.push(item);
      map.set(item.category, list);
    }
    return Array.from(map.entries());
  }, [items]);

  return (
    <div data-testid="document-template-gallery">
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{title ?? 'Start from a template'}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
        Choose a curated starting point, then customize it — or start from a blank document.
      </div>

      {grouped.map(([category, list]) => (
        <div key={category} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', marginBottom: 6 }}>
            {category}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {list.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`gallery-item-${item.id}`}
                onClick={() => onSelect(item.id)}
                style={{
                  textAlign: 'left', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12,
                  background: '#fff', cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{item.name}</div>
                <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.4 }}>{item.description}</div>
              </button>
            ))}
          </div>
        </div>
      ))}

      {onStartBlank && (
        <button
          type="button"
          data-testid="gallery-start-blank"
          onClick={onStartBlank}
          style={{ marginTop: 8, fontSize: 12, color: '#374151', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          Start from a blank document
        </button>
      )}
    </div>
  );
}

export default DocumentTemplateGallery;
